import { Link } from "react-router-dom";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";

interface BreadcrumbsProps {
  className?: string;
}

const LABEL_OVERRIDES: Record<string, string> = {
  admin: "Admin",
  signin: "Sign In",
  signup: "Sign Up",
  "set-password": "Set Password",
  "business-setup": "Business Setup",
  "business-profile": "Business Profile",
  "qr-access": "QR Access",
  "role-management": "Role Management",
  "agreement-signing": "Agreement Signing",
  "onboarding-funnel": "Onboarding Funnel",
  "platform-health": "Platform Health",
  health: "Platform Health",
  users: "All Users",
  "all-users": "All Users",
  "legal-pages": "Legal Pages",
};

function toLabel(segment: string): string {
  if (LABEL_OVERRIDES[segment]) {
    return LABEL_OVERRIDES[segment];
  }

  // Hide raw MongoDB ObjectIDs — show as "Details" instead of the hex string
  if (/^[a-f0-9]{24}$/.test(segment)) {
    return "Details";
  }

  return segment
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function Breadcrumbs({ className }: BreadcrumbsProps) {
  const pathname = useLocation().pathname;

  const crumbs = useMemo(() => {
    const cleanPath = pathname.split("?")[0];
    const segments = cleanPath.split("/").filter(Boolean);

    const items = [
      {
        href: "/",
        label: "Home",
      },
    ];

    let pathAccumulator = "";
    segments.forEach((segment) => {
      pathAccumulator += `/${segment}`;
      items.push({
        href: pathAccumulator,
        label: toLabel(segment),
      });
    });

    return items;
  }, [pathname]);

  if (pathname === "/") {
    return null;
  }

  return (
    <nav aria-label="Breadcrumb" className={className}>
      <ol className="flex flex-wrap items-center gap-1 text-xs sm:text-sm text-[var(--color-muted-foreground)]">
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            <li key={crumb.href} className="flex items-center gap-1">
              {index > 0 ? <ChevronRight className="h-3.5 w-3.5" aria-hidden /> : null}
              {isLast ? (
                <span className="font-medium text-[var(--color-foreground)]">{crumb.label}</span>
              ) : (
                <Link to={crumb.href} className="hover:text-[var(--color-foreground)] transition-colors">
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
